import type { HourSlotMap } from '@/utils/hourlyBudgetHourSlots';
import { hourSlotsFromWindows, windowsFromHourSlots } from '@/utils/hourlyBudgetHourSlots';
import type {
  HourlyBudgetBaseWindowDraft,
  HourlyBudgetTimeWindowDraft,
} from '@/utils/hourlyBudgetSchedule';

export const WEEKDAY_INDEXES = [0, 1, 2, 3, 4, 5, 6] as const;
export type WeekdayIndex = (typeof WEEKDAY_INDEXES)[number];

export type ScheduleMode = 'daily' | 'weekly';

export type DayAmounts = {
  hourly: string;
  base: string;
};

export const WEEKDAYS: { index: WeekdayIndex; short: string; long: string }[] = [
  { index: 0, short: 'Mon', long: 'Monday' },
  { index: 1, short: 'Tue', long: 'Tuesday' },
  { index: 2, short: 'Wed', long: 'Wednesday' },
  { index: 3, short: 'Thu', long: 'Thursday' },
  { index: 4, short: 'Fri', long: 'Friday' },
  { index: 5, short: 'Sat', long: 'Saturday' },
  { index: 6, short: 'Sun', long: 'Sunday' },
];

export const ALL_WEEKDAYS: WeekdayIndex[] = [...WEEKDAY_INDEXES];

export function parseScheduleMode(raw: unknown): ScheduleMode {
  return raw === 'weekly' ? 'weekly' : 'daily';
}

export function parseDaysOfWeek(raw: unknown): WeekdayIndex[] {
  if (!Array.isArray(raw) || raw.length === 0) return [...ALL_WEEKDAYS];
  const days = [
    ...new Set(
      raw
        .map((value) => Number(value))
        .filter((day): day is WeekdayIndex => Number.isInteger(day) && day >= 0 && day <= 6)
    ),
  ].sort((a, b) => a - b);
  return days.length ? days : [...ALL_WEEKDAYS];
}

export function isAllWeekdays(days: number[] | undefined): boolean {
  if (!days || days.length === 0) return true;
  return ALL_WEEKDAYS.every((day) => days.includes(day));
}

export function emptyWeeklySlots(): Record<WeekdayIndex, HourSlotMap> {
  return {
    0: {},
    1: {},
    2: {},
    3: {},
    4: {},
    5: {},
    6: {},
  };
}

export function emptyWeeklyAmounts(): Record<WeekdayIndex, DayAmounts> {
  return {
    0: { hourly: '', base: '' },
    1: { hourly: '', base: '' },
    2: { hourly: '', base: '' },
    3: { hourly: '', base: '' },
    4: { hourly: '', base: '' },
    5: { hourly: '', base: '' },
    6: { hourly: '', base: '' },
  };
}

export function cloneHourSlots(slots: HourSlotMap): HourSlotMap {
  return { ...slots };
}

export function fillWeeklySlotsFromDaily(slots: HourSlotMap): Record<WeekdayIndex, HourSlotMap> {
  const next = emptyWeeklySlots();
  for (const day of ALL_WEEKDAYS) next[day] = cloneHourSlots(slots);
  return next;
}

export function fillWeeklyAmountsFromDaily(hourly: string, base: string): Record<WeekdayIndex, DayAmounts> {
  const next = emptyWeeklyAmounts();
  for (const day of ALL_WEEKDAYS) next[day] = { hourly, base };
  return next;
}

export function allDayOneXSlots(): HourSlotMap {
  const slots: HourSlotMap = {};
  for (let hour = 0; hour < 24; hour++) slots[hour] = '1';
  return slots;
}

export function allDayWeeklySlots(): Record<WeekdayIndex, HourSlotMap> {
  return fillWeeklySlotsFromDaily(allDayOneXSlots());
}

export function weeklySlotsEqual(
  a: Record<number, HourSlotMap>,
  b: Record<number, HourSlotMap>
): boolean {
  return ALL_WEEKDAYS.every((day) => JSON.stringify(a[day] ?? {}) === JSON.stringify(b[day] ?? {}));
}

export function weeklyAmountsEqual(
  a: Record<number, DayAmounts>,
  b: Record<number, DayAmounts>
): boolean {
  return ALL_WEEKDAYS.every(
    (day) => (a[day]?.hourly ?? '') === (b[day]?.hourly ?? '') && (a[day]?.base ?? '') === (b[day]?.base ?? '')
  );
}

export function copyDayToDays<T>(source: T, days: number[], factory: (value: T) => T): Record<number, T> {
  const next: Record<number, T> = {};
  for (const day of days) next[day] = factory(source);
  return next;
}

type WindowWithDays = { start_time_local: string; end_time_local: string; days_of_week?: number[] };

export function expandWindowsToWeeklySlots(
  bases: Array<HourlyBudgetBaseWindowDraft & { days_of_week?: number[] }>,
  times: Array<HourlyBudgetTimeWindowDraft & { days_of_week?: number[] }>
): Record<WeekdayIndex, HourSlotMap> {
  const byDay = emptyWeeklySlots();
  for (const day of ALL_WEEKDAYS) {
    const dayBases = bases.filter((row) => parseDaysOfWeek(row.days_of_week).includes(day));
    const dayTimes = times.filter((row) => parseDaysOfWeek(row.days_of_week).includes(day));
    byDay[day] = hourSlotsFromWindows(dayBases, dayTimes);
  }
  return byDay;
}

function mergeByKey<T extends WindowWithDays>(
  rows: Array<T & { days_of_week: WeekdayIndex[] }>,
  keyOf: (row: T) => string
): Array<T & { days_of_week: WeekdayIndex[] }> {
  const grouped = new Map<string, T & { days_of_week: WeekdayIndex[] }>();
  for (const row of rows) {
    const key = keyOf(row);
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, { ...row, days_of_week: [...row.days_of_week] });
      continue;
    }
    const days = new Set([...existing.days_of_week, ...row.days_of_week]);
    existing.days_of_week = [...days].sort((a, b) => a - b) as WeekdayIndex[];
  }
  return [...grouped.values()];
}

export function collapseWeeklySlotsToWindows(weeklySlots: Record<number, HourSlotMap>): {
  bases: Array<HourlyBudgetBaseWindowDraft & { days_of_week: WeekdayIndex[] }>;
  windows: Array<HourlyBudgetTimeWindowDraft & { days_of_week: WeekdayIndex[] }>;
} {
  const bases: Array<HourlyBudgetBaseWindowDraft & { days_of_week: WeekdayIndex[] }> = [];
  const windows: Array<HourlyBudgetTimeWindowDraft & { days_of_week: WeekdayIndex[] }> = [];
  for (const day of ALL_WEEKDAYS) {
    const { bases: dayBases, windows: dayWindows } = windowsFromHourSlots(weeklySlots[day] ?? {});
    for (const row of dayBases) {
      bases.push({ ...row, days_of_week: [day] });
    }
    for (const row of dayWindows) {
      windows.push({ ...row, days_of_week: [day] });
    }
  }
  return {
    bases: mergeByKey(bases, (row) => `${row.start_time_local}|${row.end_time_local}`),
    windows: mergeByKey(windows, (row) => `${row.start_time_local}|${row.end_time_local}|${row.multiplier}`),
  };
}

export type DaySettingPayload = {
  day_of_week: number;
  hourly_budget: number | null;
  base_budget: number | null;
};

export function daySettingsFromWeeklyAmounts(
  weeklyAmounts: Record<number, DayAmounts>,
  parseHourly: (raw: string) => number | null,
  parseBase: (raw: string) => { value: number | null; error?: string }
): { ok: true; settings: DaySettingPayload[] } | { ok: false; error: string } {
  const settings: DaySettingPayload[] = [];
  for (const day of ALL_WEEKDAYS) {
    const amounts = weeklyAmounts[day] ?? { hourly: '', base: '' };
    const hourlyTrimmed = amounts.hourly.trim();
    let hourly: number | null = null;
    if (hourlyTrimmed) {
      hourly = parseHourly(hourlyTrimmed);
      if (hourly == null) {
        return { ok: false, error: `${WEEKDAYS[day].long}: enter a valid hourly budget greater than 0.` };
      }
    }
    const baseParsed = parseBase(amounts.base);
    if (baseParsed.error) {
      return { ok: false, error: `${WEEKDAYS[day].long}: ${baseParsed.error}` };
    }
    if (hourly == null && baseParsed.value == null) continue;
    settings.push({
      day_of_week: day,
      hourly_budget: hourly,
      base_budget: baseParsed.value,
    });
  }
  return { ok: true, settings };
}

export function weeklyAmountsFromApi(
  daySettings: unknown,
  fallbackHourly: string,
  fallbackBase: string
): Record<WeekdayIndex, DayAmounts> {
  if (!Array.isArray(daySettings) || daySettings.length === 0) {
    return fillWeeklyAmountsFromDaily(fallbackHourly, fallbackBase);
  }
  const next = emptyWeeklyAmounts();
  for (const item of daySettings) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const day = Number(row.day_of_week);
    if (!Number.isInteger(day) || day < 0 || day > 6) continue;
    next[day as WeekdayIndex] = {
      hourly: row.hourly_budget != null && row.hourly_budget !== '' ? String(row.hourly_budget) : '',
      base: row.base_budget != null && row.base_budget !== '' ? String(row.base_budget) : '',
    };
  }
  return next;
}

function numericOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function uniformWeeklyHourlyEdit(
  daySettings: unknown,
  fallbackHourly: unknown,
  fallbackBase: unknown
): { uniform: true; settings: DaySettingPayload[] } | { uniform: false } {
  const parentHourly = numericOrNull(fallbackHourly);
  const parentBase = numericOrNull(fallbackBase);
  if (!Array.isArray(daySettings) || daySettings.length === 0) {
    return {
      uniform: true,
      settings: ALL_WEEKDAYS.map((day) => ({
        day_of_week: day,
        hourly_budget: parentHourly,
        base_budget: parentBase,
      })),
    };
  }
  if (daySettings.length !== 7) return { uniform: false };
  const settings: DaySettingPayload[] = [];
  let hourly: number | null | undefined;
  for (const item of daySettings) {
    if (!item || typeof item !== 'object') return { uniform: false };
    const row = item as Record<string, unknown>;
    const day = Number(row.day_of_week);
    if (!Number.isInteger(day) || day < 0 || day > 6) return { uniform: false };
    const dayHourly = numericOrNull(row.hourly_budget);
    if (hourly === undefined) hourly = dayHourly;
    else if (hourly !== dayHourly) return { uniform: false };
    settings.push({
      day_of_week: day,
      hourly_budget: dayHourly,
      base_budget: numericOrNull(row.base_budget),
    });
  }
  const days = new Set(settings.map((row) => row.day_of_week));
  if (days.size !== 7) return { uniform: false };
  return { uniform: true, settings };
}

export function formatWeekdayRange(days: number[]): string {
  const sorted = [...new Set(days.filter((day) => day >= 0 && day <= 6))].sort((a, b) => a - b);
  if (sorted.length === 0 || sorted.length === 7) return 'Every day';
  const labels = sorted.map((day) => WEEKDAYS[day as WeekdayIndex].short);
  const contiguous =
    sorted.length > 1 && sorted.every((day, index) => index === 0 || day === sorted[index - 1] + 1);
  if (contiguous && sorted.length >= 3) {
    return `${labels[0]}–${labels[labels.length - 1]}`;
  }
  return labels.join(', ');
}
